import * as React from 'react';

import SuperCritical from 'lib/supercritical';
import { tzmoment } from 'lib/tzmoment';
import { noDefault } from 'lib/eventWrappers';


import {
    ItineraryViewModel,
    BerthReservationViewModel,
    PilotJobOrderViewModel,
    PortCallReport,
    PortCallReportViewModel,
    PortCallVesselViewModel
} from './portcallreport';

import { PCModal } from '../pcmodal/view';

export const PrintPortCallReportLink = SuperCritical(({ model: m } : { model : PortCallReport }) =>
    m.LinkAvailable() ?
        <div className="action-item">
            <div> 
                {m.Loading() ?
                    <a href="" onClick={noDefault(() => {})}>Printing...</a> :
                    <a href="" onClick={noDefault(m.PrintOrder)}>Print PortCall Report</a>
                    // <a href="" onClick={noDefault(m.derp)}>Print PortCall Report</a>
                }
            </div>
            <div className="pc-action-item-descr">
                Print a report for this portcall.
            </div>
        </div> :
        null
);

export const PortCallReportView = SuperCritical(({ model: m } : { model : PortCallReport }) =>
    <div>
        <ReportUnavailablePopup model={m} />
        <PortCallReportPrintView model={m} />
    </div>
);

const ReportUnavailablePopup = SuperCritical(({ model: m } : { model : PortCallReport }) =>
    <PCModal shown={m.ShowReportUnavailableMsg} className="portcall-report-unavailable" onBackDropClick={() => m?.ShowReportUnavailableMsg(false)}>
        <div className="modal-content pc-card">
            <div>
                Sorry, the receipt you requested is not available for this portcall.
            </div>
            <div className="controls">
                <button
                    className="pc-button"
                    onClick={() => m.ShowReportUnavailableMsg(false)}
                >
                    OK
                </button>
            </div>
        </div>
    </PCModal>
);

const PortCallReportPrintView = SuperCritical(({ model: m } : { model : PortCallReport }) => {
    const portcallReport = m.DisplayedPortCall();
    if (!portcallReport) return null;

    return (
        <div className="portcall-report">
            <PrintViewTitle portcall={portcallReport} />
            <PrintViewSubtitle portcall={portcallReport} />
            <PortCallReportSubmission portcall={portcallReport} />
            <PortCallReportVessel portcall={portcallReport} />
            <PortCallReportReservations portcall={portcallReport} />
            <PortCallReportAgentAndBilling portcall={portcallReport} />
        </div>
    );
});

const PrintViewTitle = SuperCritical(({ portcall: p} : { portcall : PortCallReportViewModel }) => 
    <h1 className="portcall-report-title">
        PortCall Report &mdash;
        {' '}
        {p.Model.DisplayVesselIdentifier()}
        {'   '}
        <span className="portcall-report-title-date">(PC {p.PCId()})</span>
    </h1>
);

const PrintViewSubtitle = SuperCritical(({ portcall: p} : { portcall : PortCallReportViewModel }) => 
    <h4 className="portcall-report-subtitle">
        {p.Port()}
    </h4>
);

const PortCallReportSubmission = SuperCritical(({ portcall: p} : { portcall : PortCallReportViewModel }) => {
    const
        requestor = p.Model.Requestor(),
        requestingOrg = requestor && requestor.Organization(),
        reservations = p.Model.Reservations(),
        requestDate = reservations.length ? reservations[0].RequestDate() : null;

    return (
        <div className="portcall-report-submission">
            <div>
                Submitted by:
                {' '}
                {requestor && requestor.FullName()}
                {', '}
                {requestingOrg && requestingOrg.Name()}
            </div>
            <div>
                Date submitted:
                {' '}
                <span className="submitted-date">
                    {requestDate && tzmoment(requestDate).format('DD MMM YYYY')}
                </span>
            </div>
        </div>
    );
});

const PortCallReportVessel = SuperCritical(({ portcall: p} : { portcall : PortCallReportViewModel }) => {
    const v = p.Vessel();

    return (
        <div className="portcall-report-section portcall-report-vessel">
            <h2 className="portcall-report-section-header">{p.Model.DisplayVesselIdentifier()}</h2>
            <div className="portcall-report-section-body">
                <div className="vessel-details">
                    <div className="details-left-col">
                        <LengthOverall portcall={p} />
                        <Beam vessel={v} />
                        <GrossTonnage vessel={v} />
                        <Flag vessel={v} />
                        <VesselType vessel={v} />
                    </div>
                    <div className="details-right-col">
                        <IMO vessel={v} />
                        <MMSID vessel={v} />
                        <CallSign vessel={v} />
                        <LastPort portcall={p} />
                        <NextPort portcall={p} />
                    </div>
                </div>
            </div>
        </div>
    );
});

const LengthOverall = SuperCritical(({ portcall: p} : { portcall :PortCallReportViewModel }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Length Overall:</span>
        <span className="vessel-section-item-value">{p.LengthDisplay()}</span>
    </div>
);

const Beam = SuperCritical(({ vessel: v } : { vessel : PortCallVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Beam:</span>
        <span className="vessel-section-item-value">{v && v.BeamDisplay()}</span>
    </div>
);

const GrossTonnage = SuperCritical(({ vessel: v } : { vessel : PortCallVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Gross Tonnage:</span>
        <span className="vessel-section-item-value">{v && v.GrossTonnageDisplay()} grt</span>
    </div>
);

const Flag = SuperCritical(({ vessel: v } : { vessel : PortCallVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Flag:</span>
        <span className="vessel-section-item-value">{v && v.Model.Flag() && v.Model.Flag()!.Name()}</span>
    </div>
);

const VesselType = SuperCritical(({ vessel: v } : { vessel : PortCallVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Vessel Type:</span>
        <span className="vessel-section-item-value">{v && v.Model.VesselType()}</span>
        {v && v.Model.IsCruiseVessel() && 
            <span className="vessel-section-item-label">This is a PortCall specific cruise ship.</span>
        }
    </div>
);

const IMO = SuperCritical(({ vessel: v } : { vessel : PortCallVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">IMO:</span>
        <span className="vessel-section-item-value">{v && v.Model.IMO()}</span>
    </div>
);

const MMSID = SuperCritical(({ vessel: v } : { vessel : PortCallVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">MMSID:</span>
        <span className="vessel-section-item-value">{v && v.Model.MMSID()}</span>
    </div>
);

const CallSign = SuperCritical(({ vessel: v } : { vessel : PortCallVesselViewModel | null }) =>
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Call Sign:</span>
        <span className="vessel-section-item-value">{v && v.Model.CallSign()}</span>
    </div>
);

const LastPort = SuperCritical(({ portcall: p } : { portcall : PortCallReportViewModel }) => 
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Last Port:</span>
        <span className="vessel-section-item-value">{p.Model.LastPortOfCall()}</span>
    </div>
);

const NextPort = SuperCritical(({ portcall: p } : { portcall : PortCallReportViewModel }) => 
    <div className="vessel-section-item">
        <span className="vessel-section-item-label">Next Port:</span>
        <span className="vessel-section-item-value">{p.Model.NextPortOfCall()}</span>
    </div>
);

const PortCallReportReservations = SuperCritical(({ portcall: p } : { portcall : PortCallReportViewModel }) =>
    <div className="portcall-report-section portcall-report-reservations">
        <h2 className="portcall-report-section-header">Reservations</h2>
        <div className="portcall-report-section-body">
            {p.ItineraryPoints().map(ip => 
                <ItineraryView portcall={p} itineraryPoint={ip} key={ip.Key} />
            )}
        </div>
    </div>
);

const ItineraryView = SuperCritical(({ portcall: p, itineraryPoint: ip } : { portcall : PortCallReportViewModel, itineraryPoint : ItineraryViewModel }) => {
    const
        bRes = ip.BerthRes,
        pjOrder = ip.PilotJobOrder();

    return (
        <div className="portcall-report-itinerary">
            {ip.PortArrival &&
                <div className="itinerary-container">
                    <div className="itinerary-date-full">
                        <div className="itinerary-date">
                            {tzmoment(ip.PortArrival.Departure.Estimated()).format('MMM D, YYYY')}
                        </div>
                    </div>
                    <div className="itinerary-time-container itinerary-col-headers">
                        <div className="itinerary-time">
                            EST
                        </div>
                        <div className="itinerary-time">
                            ACT
                        </div>
                    </div>
                    <div className="itinerary-details"></div>
                    <div className="itinerary-time-container">
                        <div className="itinerary-time">
                            {tzmoment(ip.PortArrival.Departure.Estimated()).format('HH:mm')}
                        </div>
                        {ip.PortArrival.Departure.Actual() && 
                            <div className="itinerary-time">
                                {tzmoment(ip.PortArrival.Departure.Actual()!).format('HH:mm')}
                            </div>
                        }
                    </div>
                    <div className="itinerary-details">
                        <div className="itinerary-location">
                            <div className="itinerary-location-item">
                                <span className="itinerary-location-first-arrival">Area Arrival (Pilot Station)</span>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {pjOrder && !bRes &&
                <PilotJobOrderView pilotJobOrder={pjOrder} />
            } 
            {bRes && // Berth Res
                <div className="itinerary-container">
                    {/* DC TODO -- Logic is still off when I need to show the date on tranits that move into the next day. */}
                    {!ip.IsSameDayTransit && 
                        <div className="itinerary-date-full">
                            <div className="itinerary-date">
                                {tzmoment(ip.FirstLineEst()).format('MMM D, YYYY')}
                            </div>
                        </div>
                    }
                    <div className="itinerary-time-container">
                        <div className="itinerary-time">
                            {tzmoment(ip.FirstLineEst()).format('HH:mm')}
                        </div>
                        {ip.FirstLineAct() &&
                            <div className="itinerary-time">
                                {tzmoment(ip.FirstLineAct()!).format('HH:mm')}
                            </div>
                        }
                    </div>
                    <div className="itinerary-details">
                        <div className="itinerary-location">
                            <div className="itinerary-location-item">
                                <span className="itinerary-location-item-label">Arrival</span>
                                <span className="itinerary-location-item-value">{bRes && bRes!.Location() && bRes.Location()!.Name()}</span>
                            </div>
                        </div>
                        <BerthReservationView berthReservation={ip.Reservation()} />
                    </div>
                    {!ip.isSameDayReservation &&
                        <div className="itinerary-date-full">
                            <div className="itinerary-date">
                                {tzmoment(ip.LastLineEst()).format('MMM D, YYYY')}
                            </div>
                        </div>
                    }
                    <div className="itinerary-time-container">
                        <div className="itinerary-time">
                            {tzmoment(ip.LastLineEst()).format('HH:mm')}  
                        </div>
                        {ip.LastLineAct() &&
                            <div className="itinerary-time">
                                {tzmoment(ip.LastLineAct()!).format('HH:mm')}  
                            </div>
                        }
                    </div>
                    <div className="itinerary-details">
                        <div className="itinerary-location">
                            <div className="itinerary-location-item">
                                <span className="itinerary-location-item-label">Departure</span>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {pjOrder && bRes &&
                <PilotJobOrderView pilotJobOrder={pjOrder} />
            }
        </div>
    );
});

const BerthReservationView = SuperCritical(({berthReservation: berthRes } : { berthReservation : BerthReservationViewModel | null }) => {
    const
        res = berthRes;
    return (
        <div className="res-details">
            <div className="res-details-item">
                <span className="res-details-item-label">Side to:</span>
                <span className="res-details-item-value">{res && res.SideToText()}</span>
            </div>
            <div className="res-details-item">
                <span className="res-details-item-label">Footmarks:</span>
                <span className="res-details-item-value">{`${res && res.FootmarkNum()} - ${res && res.EndmarkNum()}`}</span>
            </div>
            {res && res.Cargo().length == 0 && res.Cruise().length == 0 &&
                <div className="res-details-item">
                    <span className="res-details-item-label">Lay (no ops listed)</span>
                </div>
            }
            {res && res.Cargo().length > 0 &&
                <div className="res-details-inport-ops inport-ops-labels">
                    <div className="res-details-inport-ops-col inport-ops-activity">
                        <span className="res-details-item-label">Activity</span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-cargo">
                        <span className="res-details-item-label">Cargo</span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-quantity">
                        <span className="res-details-item-label">Quantity</span>
                    </div>
                </div>    
            }
            {res && res.Cargo().map(c => 
                <div className="res-details-inport-ops" key={c.Id()}>
                    <div className="res-details-inport-ops-col inport-ops-activity">
                        <span className="res-details-item-value">
                            {c.ActivityType()?.Name()}
                            {c.TradeRoute() && ' (' + c.TradeRoute()?.Name() + ')'}
                        </span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-cargo">
                        <span className="res-details-item-value">{c.CargoCategory()?.Name()}</span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-quantity">
                        <span className="res-details-item-value">{c.Quantity.Num() + ' ' + c.Quantity.UOM()}</span>
                    </div>
                </div>    
            )}
            {res && res.Cruise().length > 0 &&
                <div className="res-details-inport-ops inport-ops-labels">
                    <div className="res-details-inport-ops-col inport-ops-cruise">
                        <span className="res-details-item-label">Voyage #</span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-cruise">
                        <span className="res-details-item-label">Disembark</span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-cruise">
                        <span className="res-details-item-label">Embark</span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-cruise">
                        <span className="res-details-item-label">In-Transit</span>
                    </div>
                </div>    
            }
            {res && res.Cruise().map(c => 
                <div className="res-details-inport-ops" key={c.Id()}>
                    <div className="res-details-inport-ops-col inport-ops-cruise">
                        <span className="res-details-item-value">{c.VoyageNumber()}</span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-cruise">
                        <span className="res-details-item-value">{c.Disembark()}</span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-cruise">
                        <span className="res-details-item-value">{c.Embark()}</span>
                    </div>
                    <div className="res-details-inport-ops-col inport-ops-cruise">
                        <span className="res-details-item-value">{c.InTransit()}</span>
                    </div>
                </div>    
            )}
        </div>
    );
});

const PilotJobOrderView = SuperCritical(({pilotJobOrder: pjo} : {pilotJobOrder : PilotJobOrderViewModel}) => {
    return (
         <div className="pilot-container">
            <div className="pilot-details">
                <div className="pilot-details-item">
                    <span className="pilot-details-item-label">Deep Draft: </span>
                    <span className="pilot-details-item-value">{pjo.DeepDraftNum()} {pjo.DeepDraftUOM()}</span>
                </div>
                <div className="res-details-item">
                    <span className="res-details-item-label">Air Draft: </span>
                    <span className="res-details-item-value">{pjo.AirDraftNum()} {pjo.AirDraftUOM()}</span>
                </div>
                <div className="res-details-item">
                    <span className="res-details-item-label">Tug Co: </span>
                    <span className="res-details-item-value">{pjo.TugName()}</span>
                </div>
                <div className="res-details-item">
                    <span className="res-details-item-label">Line: </span>
                    <span className="res-details-item-value">{pjo.LineName()}</span>
                </div>
                <div className="res-details-item">
                    <span className="res-details-item-label">Labor: </span>
                    <span className="res-details-item-value">{pjo.LaborDT().toLocaleString()}</span>
                </div>
                <div className="res-details-item">
                    <span className="res-details-item-label">Remarks: </span>
                    <span className="res-details-item-value">{pjo.Remarks()}</span>
                </div>
            </div>
        </div> 
    );
});

const PortCallReportAgentAndBilling = SuperCritical(({ portcall: p} : { portcall : PortCallReportViewModel }) =>
    <div className="portcall-report-section portcall-report-agent-jobs">
        <h2 className="portcall-report-section-header">
            <span className="agent-header">Agent</span>
            {/* <span className="jobs-header">Job Orders &amp; Coordinations</span> */}
        </h2>
        <div className="portcall-report-section-body">
            <AgentView portcall={p} />
            {p.Model.JobOrders().length > 0 &&
                <BillToView portcall={p} />
            }
            {/* <JobOrdersEtc portcall={p} /> */}
            {/* <RemarksView portcall={p} /> */}
        </div>
        {/* {p.Model.JobOrders().length > 0 &&
            // If used; needs spacing from Agent section
            <div>
                <h2 className="portcall-report-section-header">
                    <span className="agent-header">Bill To</span>
                </h2>
                <div className="portcall-report-section-body">
                    <BillToView portcall={p} />
                </div>
            </div>
        }*/}
    </div>
);

const AgentView = SuperCritical(({ portcall: p} : { portcall : PortCallReportViewModel }) => {
    const agent = p.Model.Agent();
    return (
        <div className="agent">
            {agent &&
                <div>
                    <div className="agent-name">{agent.FullName()}</div>
                    <div className="agent-contact-info">
                        <div>{agent.Organization() && agent.Organization()!.Name()}</div>
                        <div className="agent-email">{agent.User() && agent.User()!.Email()}</div>
                        <div>{agent.MainPhoneNumber() && agent.MainPhoneNumber()!.Number()}</div>
                    </div>
                </div>
            }
        </div>
    );
});

const BillToView = SuperCritical(({ portcall: p} : { portcall : PortCallReportViewModel }) => {
    const pjo = p.Model.JobOrders()[0];
    return (
        <div className="agent">
            {pjo &&
                <div>
                    {/* <div className="agent-name">Billing Agent</div>
                    <div className="agent-contact-info">
                        <div>{pjo.PilotJobOrder()?.BillToAgent() ?? '--'}</div>
                    </div> */}
                    <div className="agent-name">Billing Address</div>
                    <div className="agent-contact-info">
                        {/** TODO: all one line; how to put into standard form */}
                        <div>{pjo.PilotJobOrder()?.BillToAddress() ?? ''}</div>
                    </div>
                </div>
            }
        </div>
    );
});

// const JobOrdersEtc = SuperCritical(({ portcall: p} : { portcall : PortCallReportViewModel }) => {
//     const pjos = p.PJOs;
//     return (
//         <div className="jobs-etc">
//             {pjos > 0 &&
//                 <div>
//                    {pjos} Pilot Job Order{pjos > 0 ? 's ' : ' '}present
//                 </div>
//             }
//             {pjo &&
//                 <div>
//                     <BillToAddress pjo={pjo} />
//                     <ServicesView pjo={pjo} />
//                 </div>
//             }
//         </div>
//     );
// });

// const BillToAddress = SuperCritical(({ pjo } : { pjo : PortCallReportViewModel }) => {
//     const billToAddr = pjo.Model.BillToAddress();
//     return billToAddr ?
//         <div className="bill-to-address">
//             {pjo.Model.BillToAddress() || ''}
//         </div> :
//         null
// });

// const ServicesView = SuperCritical(({ pjo } : { pjo : PortCallReportViewModel }) =>
//     <div className="services">
//         <div className="services-item">
//             <span className="services-item-label">Tug:</span>
//             {/* <span className="services-item-value">{pjo.Model.TugCompany() && pjo.Model.TugCompany()!.Name()}</span> */}
//         </div>
//         <div className="services-item">
//             <span className="services-item-label">Tug #:</span>
//             {/* <span className="services-item-value">{pjo.Model.NumberOfTugs()}</span> */}
//         </div>
//         <div className="services-item">
//             <span className="services-item-label">Lines:</span>
//             {/* <span className="services-item-value">{pjo.Model.LineHandlingCompany() && pjo.Model.LineHandlingCompany()!.Name()}</span> */}
//         </div>
//         <div className="services-item">
//             <span className="services-item-label">Labor:</span>
//             {/* <span className="services-item-value">{pjo.Model.LaborStartTime() && tzmoment(pjo.Model.LaborStartTime()!).format("MMM D, YYYY [at] HH:mm")}</span> */}
//         </div>
//     </div>
// );

// const RemarksView = SuperCritical(({ portcall: p} : { portcall : PortCallReportViewModel }) =>
// Below the Agent info
//     <div className="remarks">
//         <span className="remarks-label">Remarks:</span>
//         <span className="remarks-value">
//             Remarks section just so I know.
//             {/* {p.TypicalPilotJobOrder() && p.TypicalPilotJobOrder()!.Model.Remarks()} */}
//         </span>
//     </div>
// );



// WEBPACK FOOTER //
// ./apps/portcall/portcallprofile/portcallreportview.tsx